/**
 * SmoothParticleEmitter — a calm ambient particle stream where every
 * particle's scale, alpha, travel and colour ride EASED curves (the brief's
 * "smooth particles and movement"). Soft `glow_soft` sprites, additive blend,
 * a steady spawn cadence, a sprite POOL so a long-running stream never leaks.
 *
 * Drive it with update(dtMs) — either hand-pumped (tests) or wired to
 * PixiOverlayHost.addTickListener via attachTo(). All the per-particle math
 * lives in smoothEmitterConfig (pure, unit-tested); this class only pools +
 * applies it. Pixi stays behind the seam (createSprite + ISprite).
 */
import { createSprite } from '../../rendering/utils/Sprite';
import type { ISprite, SpriteOptions } from '../../rendering/IRenderer';
import { Z_LAYERS } from '../../rendering/Z_LAYERS';
import {
  resolveSmoothEmitter,
  particleAt,
  type SmoothEmitterConfig,
  type SmoothEmitterSpec,
  type ParticleSeed,
} from './smoothEmitterConfig';
import { seededRand } from '../atmosphere/atmospherePlacement';

export interface SmoothEmitterRenderConfig extends SmoothEmitterConfig {
  /** Emit-angle band (degrees, screen space: -90 = straight up). */
  angleMinDeg?: number;
  angleMaxDeg?: number;
  /** Spawn-area half-extent (px) around the origin, for a soft column. */
  spreadX?: number;
  spreadY?: number;
  /** Base particle sprite diameter (px). */
  size?: number;
}

export interface SmoothEmitterOptions {
  depth?: number;
  spriteFactory?: (opts: SpriteOptions) => ISprite | null;
  /** Optional per-layer display container (Scene FX v3 M1). Forwarded into every
   *  spawned particle's SpriteOptions so the layer's container.alpha dims them. */
  parent?: { addChild(child: unknown): void };
}

interface PooledParticle {
  sprite: ISprite;
  seed: ParticleSeed;
  ageMs: number;
  alive: boolean;
}

const RENDER_DEFAULTS = {
  angleMinDeg: -100,
  angleMaxDeg: -80, // a gentle upward fan by default (rising motes)
  spreadX: 18,
  spreadY: 8,
  size: 26,
};

export class SmoothParticleEmitter {
  private spec: SmoothEmitterSpec;
  private readonly angleMinDeg: number;
  private readonly angleMaxDeg: number;
  private readonly spreadX: number;
  private readonly spreadY: number;
  private readonly size: number;
  private readonly depth: number;
  private readonly factory: (opts: SpriteOptions) => ISprite | null;
  private readonly parent?: { addChild(child: unknown): void };

  private origin: { x: number; y: number };
  private particles: PooledParticle[] = [];
  private pool: ISprite[] = [];
  private elapsedMs = 0;
  private spawnAccumMs = 0; // fractional spawn debt (frame-rate + density-scale independent)
  private densityScale = 1; // runtime spawn-rate multiplier (modulation)
  private emitting = true;
  private destroyed = false;
  private spawnSeed = 1;
  private detach: (() => void) | null = null;

  constructor(origin: { x: number; y: number }, cfg: SmoothEmitterRenderConfig, opts: SmoothEmitterOptions = {}) {
    this.origin = { ...origin };
    this.spec = resolveSmoothEmitter(cfg);
    this.angleMinDeg = cfg.angleMinDeg ?? RENDER_DEFAULTS.angleMinDeg;
    this.angleMaxDeg = cfg.angleMaxDeg ?? RENDER_DEFAULTS.angleMaxDeg;
    this.spreadX = cfg.spreadX ?? RENDER_DEFAULTS.spreadX;
    this.spreadY = cfg.spreadY ?? RENDER_DEFAULTS.spreadY;
    this.size = cfg.size ?? RENDER_DEFAULTS.size;
    this.depth = opts.depth ?? Z_LAYERS.ATMOSPHERE;
    this.factory = opts.spriteFactory ?? ((o: SpriteOptions) => createSprite(null, o));
    this.parent = opts.parent;
  }

  /** Wire to a ticker (PixiOverlayHost.addTickListener returns an unsub). */
  attachTo(addTickListener: (fn: (dtMs: number) => void) => () => void): void {
    this.detach?.();
    this.detach = addTickListener((dt) => this.update(dt));
  }

  /** Advance the stream by dtMs: recycle dead particles, spawn on cadence,
   *  apply the eased per-particle state to every live sprite. */
  update(dtMs: number): void {
    if (this.destroyed) return;
    this.elapsedMs += dtMs;

    // Age + recycle.
    for (const p of this.particles) {
      if (!p.alive) continue;
      p.ageMs += dtMs;
      if (p.ageMs >= this.spec.lifespanMs) {
        this.recycle(p);
      } else {
        this.applyState(p);
      }
    }

    // Spawn on cadence — a fractional debt accumulator so the effective period
    // (frequencyMs / densityScale) can vary smoothly frame-to-frame without the
    // boundary jitter a floor(elapsed/freq) counter shows under a changing freq.
    if (this.emitting) {
      const freq = this.spec.frequencyMs / Math.max(1e-3, this.densityScale);
      if (freq > 0) {
        this.spawnAccumMs += dtMs;
        // Bound the carry-over so a pathological density spike can't accrue
        // unbounded debt (the loop guard caps spawns/frame; this caps backlog).
        if (this.spawnAccumMs > freq * 64) this.spawnAccumMs = freq * 64;
        let guard = 0;
        while (this.spawnAccumMs >= freq && guard < 256) { this.spawn(); this.spawnAccumMs -= freq; guard += 1; }
      }
    }
  }

  /** Runs the stream forward one particle lifespan in fixed steps, so it is full along its whole
   *  path at once (a reduced-motion caller then holds it there by never attaching a ticker).
   *  Spawns are seeded, so the same origin always gives the same field. */
  prewarm(stepMs = 50): void {
    for (let t = 0; t < this.spec.lifespanMs; t += stepMs) this.update(stepMs);
  }

  /** Modulation hook — scale spawn density (1 = authored cadence; >1 = denser). */
  setDensityScale(mult: number): void {
    this.densityScale = Math.max(0, mult);
  }

  liveCount(): number {
    let c = 0;
    for (const p of this.particles) if (p.alive) c += 1;
    return c;
  }

  setPosition(x: number, y: number): void {
    this.origin.x = x;
    this.origin.y = y;
  }

  /** Stop spawning; live particles finish their life. */
  stop(): void {
    this.emitting = false;
  }

  /** Resume spawning. */
  start(): void {
    this.emitting = true;
  }

  destroy(): void {
    if (this.destroyed) return;
    this.destroyed = true;
    this.detach?.();
    this.detach = null;
    for (const p of this.particles) {
      try { p.sprite.destroy(); } catch { /* gone */ }
    }
    for (const s of this.pool) {
      try { s.destroy(); } catch { /* gone */ }
    }
    this.particles = [];
    this.pool = [];
  }

  // ── internals ───────────────────────────────────────────────────────────

  private spawn(): void {
    const s = (this.spawnSeed += 1);
    const angDeg = this.angleMinDeg + (this.angleMaxDeg - this.angleMinDeg) * seededRand(s * 3.1);
    const seed: ParticleSeed = {
      x: this.origin.x + (seededRand(s * 1.7) * 2 - 1) * this.spreadX,
      y: this.origin.y + (seededRand(s * 2.3) * 2 - 1) * this.spreadY,
      angleRad: (angDeg * Math.PI) / 180,
    };

    // Reuse a pooled sprite or mint one.
    let sprite = this.pool.pop() ?? null;
    if (!sprite) {
      sprite = this.factory({
        x: seed.x,
        y: seed.y,
        initialTextureKey: 'glow_soft',
        width: this.size,
        height: this.size,
        depth: this.depth,
        alpha: this.spec.alphaStart,
        parent: this.parent,
      });
      if (!sprite) return; // texture not ready — skip this particle
      sprite.setBlendMode(this.spec.blendMode);
    }

    const p: PooledParticle = { sprite, seed, ageMs: 0, alive: true };
    this.particles.push(p);
    this.applyState(p);
  }

  private applyState(p: PooledParticle): void {
    const t = p.ageMs / this.spec.lifespanMs;
    const st = particleAt(this.spec, p.seed, t);
    p.sprite.setPosition(st.x, st.y);
    p.sprite.setScale(st.scale);
    p.sprite.setAlpha(st.alpha);
    p.sprite.setTint(st.color);
  }

  private recycle(p: PooledParticle): void {
    p.alive = false;
    try {
      // Park by alpha only. Visibility flips rebuild Pixi's instruction set
      // twice per particle life; applyState restores the next life on reuse.
      p.sprite.setAlpha(0);
    } catch { /* gone */ }
    this.pool.push(p.sprite);
    // Compact the live list lazily — drop the recycled entry.
    const idx = this.particles.indexOf(p);
    if (idx >= 0) this.particles.splice(idx, 1);
  }
}
